import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Search, BarChart3, FileCheck, Sparkles, Rocket } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { t } from '@/i18n/translations';

const stepIcons = [Search, BarChart3, FileCheck, Sparkles, Rocket];
const stepImages = [
  'https://customer-assets.emergentagent.com/job_homewise-ticino/artifacts/ae3uk4xc_IMG-20260411-WA0091.jpg',
  'https://images.unsplash.com/photo-1757439402224-56c48352f719?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGRlc2lnbiUyMGNsZWFuJTIwbWluaW1hbHxlbnwwfHx8fDE3NzUxNjk0OTh8MA&ixlib=rb-4.1.0&q=85',
  'https://customer-assets.emergentagent.com/job_homewise-ticino/artifacts/ogkubpwr_IMG-20260411-WA0089.jpg',
  'https://images.unsplash.com/photo-1757439402103-fc35542f96f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGRlc2lnbiUyMGNsZWFuJTIwbWluaW1hbHxlbnwwfHx8fDE3NzUxNjk0OTh8MA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1757262798677-ab4af4455a58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBicmlnaHQlMjBsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc1MTY5MjA1fDA&ixlib=rb-4.1.0&q=85'
];

const ComeFunziona = () => {
  const { lang } = useLanguage();
  const c = t('comeFunziona', lang);
  const fadeIn = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };

  return (
    <div className="bg-white pt-32 pb-16" data-testid="come-funziona-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeIn} className="text-center mb-16">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0A1312] mb-6" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{c.title}</h1>
          <p className="text-base md:text-lg text-[#0A1312] max-w-3xl mx-auto" style={{fontFamily: "'Satoshi', sans-serif"}}>{c.subtitle}</p>
        </motion.div>

        <div className="space-y-12 mb-16">
          {c.steps.map((step, idx) => {
            const Icon = stepIcons[idx];
            return (
              <motion.div key={step.number} {...fadeIn} transition={{ delay: idx * 0.1 }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-testid={`step-${idx}`}>
                <div className={`lg:col-span-5 ${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="bg-[#F7F9F9] p-8 border border-[#E2E8F0] rounded-sm">
                    <div className="flex items-center mb-4">
                      <div className="text-5xl font-bold text-[#65c3ba] mr-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{step.number}</div>
                      <Icon className="text-[#65c3ba]" size={40} strokeWidth={1.5} />
                    </div>
                    <h2 className="text-2xl font-bold text-[#0A1312] mb-3" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{step.title}</h2>
                    <p className="text-[#0A1312] mb-4" style={{fontFamily: "'Satoshi', sans-serif"}}>{step.desc}</p>
                    <div className="inline-block bg-[#65c3ba] text-white px-4 py-1 rounded-sm text-sm mb-4">{step.duration}</div>
                    <ul className="space-y-2">
                      {step.details.map((d, dIdx) => (
                        <li key={`detail-${d.slice(0,15)}`} className="flex items-start text-[#0A1312] text-sm">
                          <span className="text-[#65c3ba] mr-2">{'\u2713'}</span>
                          <span style={{fontFamily: "'Satoshi', sans-serif"}}>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className={`lg:col-span-7 ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="relative h-64 lg:h-96 rounded-sm overflow-hidden">
                    <img src={stepImages[idx]} alt={step.title} className="w-full h-full object-cover" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <motion.div {...fadeIn} className="relative overflow-hidden text-center" data-testid="come-funziona-cta">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1756802137414-7bc6ba908e6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHx3YXJtJTIwbW9kZXJuJTIwYXBhcnRtZW50JTIwaW50ZXJpb3IlMjBjb3p5JTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3NzYwMzA1NTZ8MA&ixlib=rb-4.1.0&q=85')", backgroundAttachment: 'fixed' }} />
        <div className="absolute inset-0 bg-[#3e716c]/80" />
        <div className="relative z-10 py-16 px-4 text-white">
          <h2 className="text-2xl font-bold mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{c.ctaTitle}</h2>
          <p className="text-white/70 mb-8" style={{fontFamily: "'Satoshi', sans-serif"}}>{c.ctaText}</p>
          <Link to="/valutazione-gratuita">
            <Button className="bg-[#65c3ba] text-white hover:bg-white hover:text-[#3e716c] rounded-sm px-8 py-6 text-lg">{c.ctaBtn}</Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ComeFunziona;